// import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Flashcard, FlashcardScheduling } from '../types/Flashcard';
import { addPastFlashcard } from '../slices/pastFlashcards/pastFlashcardsSlice';
import { posthog } from 'posthog-js';
import { selectTodaySession } from '../slices/todaySession/todaySessionSlice';
import { FSRS, Rating } from '../services/scheduler';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { submitFlashcardResponse } from '../services/flashcards';
import {
  flashcardRemoved,
  flashcardUpdated,
  unshiftDue,
  unshiftNew,
} from '../slices/flashcards/flashcardsSlice';
import {
  selectPrevFlashcard,
  shiftPastFlashcard,
} from '../slices/pastFlashcards/pastFlashcardsSlice';
import { enqueueSnackbar } from 'notistack';
import { deleteFlashcardResponse } from '../services/flashcardResponse';

interface UseFlashcardResponseProps {
  currentFlashcard: Flashcard | null;
  currentFlashcardType: string;
  intervalLabels: { [key: number]: string };
  setShowErrorMessage: (show: boolean) => void;
  setIsFlipped: (isFlipped: boolean) => void;
}

export const useFlashcardResponse = ({
  currentFlashcard,
  currentFlashcardType,
  intervalLabels,
  setShowErrorMessage,
  setIsFlipped,
}: UseFlashcardResponseProps) => {
  const todaySession = useSelector(selectTodaySession);
  const prevFlashcard = useSelector(selectPrevFlashcard);
  const [flashcardStartTime, setFlashcardStartTime] = useState<Moment | null>(
    null
  );

  const dispatch = useDispatch();
  const fsrs = new FSRS();

  useEffect(() => {
    if (!flashcardStartTime) {
      setFlashcardStartTime(moment());
    }
    if (!currentFlashcard || !currentFlashcard.contents.length) {
      setShowErrorMessage(true);
    } else {
      setShowErrorMessage(false);
    }
  }, [currentFlashcard, flashcardStartTime]);

  const handleResponse = async (response: number) => {
    if (!currentFlashcard) return;

    storePastFlashcard();

    // TODO: Audit flashcard response time tracking
    const flashcardResponseTime = parseFloat(
      (moment().diff(flashcardStartTime, 'milliseconds') / 1000).toFixed(3)
    );
    setFlashcardStartTime(null);

    const newScheduling = getNewScheduling(response);
    if (!newScheduling) return;
    const flashcardResponseObject = {
      flashcardId: currentFlashcard.id,
      responseNum: response,
      studySessionId: todaySession.id,
      duration: flashcardResponseTime > 0 ? flashcardResponseTime : null,
      scheduling: newScheduling,
    };

    submitFlashcardResponse(flashcardResponseObject);

    const updatedFlashcard = {
      ...currentFlashcard,
      scheduling: newScheduling,
    };
    dispatch(flashcardUpdated(updatedFlashcard));

    renderRatingFeedback(response);
    posthog?.capture('flashcard_respond');
  };

  const undoResponse = () => {
    if (!prevFlashcard) {
      enqueueSnackbar('Maximum number of undos reached.', {
        autoHideDuration: 3000,
      });
      return;
    }
    dispatch(flashcardRemoved(prevFlashcard.flashcard));

    // Add prev flashcard back to the appropriate queue
    if (prevFlashcard.type === 'new') {
      dispatch(unshiftNew(prevFlashcard.flashcard));
    } else {
      dispatch(unshiftDue(prevFlashcard.flashcard));
    }

    dispatch(shiftPastFlashcard());
    setIsFlipped(false);

    deleteFlashcardResponse(prevFlashcard.flashcard.id, todaySession.id);

    enqueueSnackbar('Response undone.', { autoHideDuration: 3000 });
    posthog?.capture('flashcard_undo_respond');
  };

  const storePastFlashcard = () => {
    if (!currentFlashcard) return;
    const pastFlashcardInfo = {
      flashcard: currentFlashcard,
      type: currentFlashcardType,
    };
    dispatch(addPastFlashcard(pastFlashcardInfo));
  };

  const getNewScheduling = (response: number): FlashcardScheduling | null => {
    if (!currentFlashcard) return null;
    const now = new Date();
    const initialScheduling = currentFlashcard.scheduling;

    const newDifficultyIndex = calcNewDifficultyIndex(response);

    // Calculate new scheduling
    const schedulingOptions = fsrs.repeat(initialScheduling, now);

    // Convert UI response (0-3) to Rating enum (1-4)
    const ratingMap: { [key: number]: Rating } = {
      0: Rating.Again, // UI 0 -> Rating.Again (1)
      1: Rating.Hard, // UI 1 -> Rating.Hard (2)
      2: Rating.Good, // UI 2 -> Rating.Good (3)
      3: Rating.Easy, // UI 3 -> Rating.Easy (4)
    };

    const rating = ratingMap[response];
    if (!rating) {
      console.error('Invalid response value:', response);
      return null;
    }

    // Get the appropriate scheduling option using the Rating enum
    const newScheduling = schedulingOptions[rating].card;
    newScheduling.difficultyIndex = newDifficultyIndex;
    newScheduling.sessionId = todaySession.id;

    // Convert Date objects to ISO strings for Redux serialization
    if (newScheduling.due instanceof Date) {
      newScheduling.due = newScheduling.due.toISOString();
    }
    if (newScheduling.lastReview instanceof Date) {
      newScheduling.lastReview = newScheduling.lastReview.toISOString();
    }

    return newScheduling;
  };

  const calcNewDifficultyIndex = (response: number) => {
    if (!currentFlashcard) return 0;
    const numFlashcardVariants = currentFlashcard.contents.length;
    const initialDifficultyIndex = currentFlashcard.scheduling.difficultyIndex;
    if (response === 0 || response === 1) {
      return Math.max(initialDifficultyIndex - 1, 0);
    } else {
      return Math.min(initialDifficultyIndex + 1, numFlashcardVariants - 1);
    }
  };

  const renderRatingFeedback = (response: number) => {
    const feedbackTarget = document.querySelector('.flashcard_actions');
    const childDiv = document.createElement('div');
    childDiv.classList.add('rating-feedback');
    childDiv.classList.add(
      response === 2 || response === 3 ? 'right' : 'wrong'
    );
    const feedbackText = intervalLabels[response];
    if (!feedbackText) return;
    const feedbackTextFormatted = feedbackText.replace('m', 'min');
    childDiv.textContent = feedbackTextFormatted;
    feedbackTarget?.appendChild(childDiv);
    setTimeout(() => {
      childDiv.classList.add('show');
    });
    const delay = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));
    delay(500)
      .then(() => {
        childDiv.classList.add('hide');
        return new Promise((resolve) => setTimeout(resolve, 250));
      })
      .then(() => {
        feedbackTarget?.removeChild(childDiv);
      });
  };

  return { handleResponse, undoResponse };
};
